<script setup lang="ts">
    import { isByokModel, type ModelId } from 'models'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import {
        missingModelNote,
        modelAvailabilityNote,
        useModelCatalogEntry,
        useModelCatalogKnown,
    } from './modelCatalog'

    const props = defineProps<{
        modelName?: string | ModelId
        /**
         * The commercial name, when the caller has the catalog. Without it
         * the chip falls back to the id, which is the case of the widget and
         * of a message recorded before the model existed.
         */
        label?: string
        /** The endpoint name, written next to a `byok/` model. */
        endpointName?: string
        /** The catalog's icon, when the caller has it. */
        brandIcon?: string
        /**
         * The model is shown as a fact about the past (the one that answered
         * a message, the one that described a document), so whether it can
         * still be picked today is not the chip's business there, and a note
         * on every old message would be noise.
         */
        historical?: boolean
        /**
         * Name the source of a gateway model too. For a picker that offers
         * both sources, where the chosen one is exactly what the user decides
         * on; everywhere else a gateway model is the ordinary case and only
         * an endpoint model says where it runs.
         */
        showSource?: boolean
    }>()

    const { t: $t } = useI18n({ useScope: 'global' })

    /**
     * The app offers the catalog once, so a chip anywhere in it shows the
     * commercial name and the producer's logo without every caller passing
     * them. An explicit prop still wins, for a caller that already has the
     * entry in hand.
     */
    const catalogEntry = useModelCatalogEntry()
    const catalogKnown = useModelCatalogKnown()
    const entry = computed(() => catalogEntry(props.modelName))

    /**
     * Everything after the FIRST slash, not the second segment: an endpoint
     * id keeps its own slashes, and `split('/')[1]` showed `meta-llama` for
     * `byok/meta-llama/Llama-3.3-70B`.
     */
    const shortName = computed(() => {
        const id = props.modelName
        if (!id) {
            return undefined
        }
        const slash = id.indexOf('/')
        return slash === -1 ? id : id.slice(slash + 1)
    })

    const brand = computed(() => {
        const id = props.modelName
        if (!id) {
            return undefined
        }
        const slash = id.indexOf('/')
        return slash === -1 ? id : id.slice(0, slash)
    })

    /**
     * The catalog resolves the producer's icon, because it is the one place
     * that knows every producer the gateway can name. Deriving
     * `ri:<brand>-fill` here drew nothing for the ones Remix Icon does not
     * have, so without a catalog entry the chip takes a generic mark rather
     * than an empty square. A model on an organization's own endpoint takes
     * its producer's logo from the catalog too, when the price list knows it;
     * without an entry it gets the server mark.
     */
    const icon = computed(() => {
        const resolved = props.brandIcon ?? entry.value?.brandIcon
        if (resolved) {
            return resolved
        }
        return isByokModel(props.modelName) ? 'ri:server-line' : 'ri:cpu-line'
    })

    const iconPrefix = computed(() =>
        icon.value.startsWith('custom:') ? 'custom' : undefined,
    )
    const iconName = computed(() => icon.value.replace(/^custom:/, ''))

    /**
     * Where the model runs, which is who bills it: the platform's credit or
     * the organization's own provider. An endpoint model draws its
     * producer's logo like any other, so the word is what tells the two
     * apart. Kept on a historical chip too: which one answered a message is
     * a fact about that message.
     */
    const source = computed(() => {
        if (isByokModel(props.modelName)) {
            const name = props.endpointName ?? entry.value?.brand
            return name
                ? {
                      label: name,
                      hint: $t('hint.modelSourceEndpoint', { name }),
                  }
                : undefined
        }
        return props.showSource
            ? {
                  label: $t('label.modelSourcePlatform'),
                  hint: $t('hint.modelSourcePlatform'),
              }
            : undefined
    })

    /**
     * Retired, or switched off for this organization. Said on the chip
     * wherever it shows a model somebody could still be relying on (an
     * agent's, a default), which is what keeps an agent left on a switched
     * off model visibly marked instead of looking like any other.
     */
    const note = computed(() => {
        if (props.historical) {
            return undefined
        }
        if (entry.value) {
            return modelAvailabilityNote(entry.value)
        }
        return catalogKnown.value
            ? missingModelNote(props.modelName)
            : undefined
    })
</script>

<template>
    <div class="flex gap-6 items-center">
        <VvIcon
            :name="iconName"
            :prefix="iconPrefix"
            class="shrink-0"
            :class="{ 'text-word-4': note }"
            :title="
                isByokModel(modelName) ? undefined : (entry?.brand ?? brand)
            " />
        <!--
            The name and its notes sit on one baseline, the icon is centred
            on the row: centred too, the smaller notes rode higher than the
            name they belong to.

            `||` and not `??`: a catalog entry can carry an empty label (the
            gateway listing has a few), and an empty string is a name that
            draws nothing.
        -->
        <span class="flex items-baseline gap-6 min-w-0">
            <span class="truncate" :class="{ 'text-word-3': note }">
                {{ label || entry?.label || shortName }}
            </span>
            <span
                v-if="source"
                class="shrink-0 text-12 text-word-3"
                :title="source.hint">
                {{ source.label }}
            </span>
            <span
                v-if="note"
                class="shrink-0 text-12 text-word-3"
                :title="$t(note.hint)">
                {{ $t(note.label) }}
            </span>
        </span>
    </div>
</template>
